import { ChatClientType } from "build-ts/chat_session";
import { DarwinHeader, SortBy, SortOrder } from "build-ts/common";
import { Header } from "build-ts/metadata";
import showToast from "hooks/useCustomToast";
import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchListOfChatSessions, deleteChatSession as deleteChatSessionAPI } from "services/AgentChatService";
import { getListOfAnalysts } from "services/AnalystService";
import { useUser } from "./UserContext";

interface AnalystChatContextType {
	// Current chat state
	currentChatId: string;
	setCurrentChatId: React.Dispatch<React.SetStateAction<string>>;
	currentSessionHeader: DarwinHeader | undefined;
	setCurrentSessionHeader: React.Dispatch<React.SetStateAction<DarwinHeader | undefined>>;

	// Chat sessions list (FRIDA)
	chatSessionsList: DarwinHeader[];
	setChatSessionsList: React.Dispatch<React.SetStateAction<DarwinHeader[]>>;
	fetchChatSessionsList: (clientType: ChatClientType, searchQuery?: string, analyst?: Header | null) => Promise<void>;
	isLoadingChatSessions: boolean;

	// EVA chat sessions
	evaChatSessionsList: DarwinHeader[];
	setEvaChatSessionsList: React.Dispatch<React.SetStateAction<DarwinHeader[]>>;
	selectedEvaSessionId: string;
	setSelectedEvaSessionId: React.Dispatch<React.SetStateAction<string>>;
	fetchEvaChatSessionsList: (searchQuery?: string) => Promise<void>;
	isLoadingEvaSessions: boolean;

	// Analysts
	analystsList: Header[];
	setAnalystsList: React.Dispatch<React.SetStateAction<Header[]>>;
	selectedAnalyst: Header | null;
	setSelectedAnalyst: (analyst: Header | null) => void;
	isLoadingAnalysts: boolean;
	fetchAnalystsList: () => Promise<void>;

	// Actions
	selectChatSession: (sessionId: string) => void;
	deleteChatSession: (sessionId: string, clientType: ChatClientType) => Promise<void>;

	// Navbar collapsed state
	isNavbarCollapsed: boolean;
	setIsNavbarCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

const AnalystChatContext = createContext<AnalystChatContextType | undefined>(undefined);

export const AnalystChatContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { defaultAnalystId, userId } = useUser();

	// Navbar collapsed state
	const [isNavbarCollapsed, setIsNavbarCollapsed] = useState(false);

	// Current chat state
	const [currentChatId, setCurrentChatId] = useState<string>("");
	// session header state
	const [currentSessionHeader, setCurrentSessionHeader] = useState<DarwinHeader | undefined>(undefined);


	// Chat sessions list (FRIDA)
	const [chatSessionsList, setChatSessionsList] = useState<DarwinHeader[]>([]);
	const [isLoadingChatSessions, setIsLoadingChatSessions] = useState(false);

	// EVA chat sessions
	const [evaChatSessionsList, setEvaChatSessionsList] = useState<DarwinHeader[]>([]);
	const [selectedEvaSessionId, setSelectedEvaSessionId] = useState<string>("");
	const [isLoadingEvaSessions, setIsLoadingEvaSessions] = useState(false);

	// Analysts
	const [analystsList, setAnalystsList] = useState<Header[]>([]);
	const [selectedAnalyst, setSelectedAnalyst] = useState<Header | null>(null);
	const [isLoadingAnalysts, setIsLoadingAnalysts] = useState(true);

	// Fetch analysts list
	const fetchAnalystsList = async () => {
		setIsLoadingAnalysts(true);
		try {
			const response = await getListOfAnalysts(
				-1, // all analysts
				1, // page 1
				"LAST_MODIFIED", // sort by last modified
				false, // sort descending
				"", // search
				"" // author
			);
			if (response?.headers && response.headers.length > 0) {
				setAnalystsList(response.headers);
				setSelectedAnalyst(response.headers.find((analyst: Header) => analyst.id === defaultAnalystId) || response.headers[0]);
			}
		} catch (error) {
			console.error("Failed to fetch analysts:", error);
		} finally {
			setIsLoadingAnalysts(false);
		}
	};

	// Fetch chat sessions list
	// If analyst is provided, filter by that analyst; otherwise fetch all sessions
	const fetchChatSessionsList = async (clientType: ChatClientType, searchQuery?: string, analyst?: Header | null) => {
		setIsLoadingChatSessions(true);
		try {
			let associatedEntityKey: string[] = [];
			// Only filter by analyst if one is provided
			if (clientType === ChatClientType.FRIDA && analyst) {
				associatedEntityKey = [`ANALYST::${analyst.id}`];
			} else if (clientType === ChatClientType.FRIDA && defaultAnalystId) {
				associatedEntityKey = [`ANALYST::${defaultAnalystId}`];
			} else {
				associatedEntityKey = [];
			}

			const result = await fetchListOfChatSessions(
				[clientType],
				50,
				0,
				searchQuery,
				"",
				associatedEntityKey,
				SortBy.LAST_MODIFIED_DATE,
				SortOrder.DESC,
				userId
			);
			setChatSessionsList(result.headers);
		} catch (error) {
			showToast("Failed to fetch chat sessions", "", "error");
			console.error("Failed to fetch chat sessions:", error);
		} finally {
			setIsLoadingChatSessions(false);
		}
	};

	// Fetch EVA chat sessions list
	const fetchEvaChatSessionsList = async (searchQuery?: string) => {
		setIsLoadingEvaSessions(true);
		try {
			const result = await fetchListOfChatSessions(
				[ChatClientType.EVA],
				100,
				0,
				searchQuery,
				"",
				undefined, // No associatedEntityKey for EVA
				SortBy.LAST_MODIFIED_DATE,
				SortOrder.DESC,
				userId
			);
			setEvaChatSessionsList(result.headers);
		} catch (error) {
			showToast("Failed to fetch EVA chat sessions", "", "error");
			console.error("Failed to fetch EVA chat sessions:", error);
		} finally {
			setIsLoadingEvaSessions(false);
		}
	};

	// Fetch analysts on mount
	useEffect(() => {
		fetchAnalystsList();
	}, []);

	// Fetch ALL chat sessions on mount (without analyst filter)
	useEffect(() => {
		fetchChatSessionsList(ChatClientType.FRIDA, "");
	}, []);

	// Select a chat session and navigate to it
	const selectChatSession = (sessionId: string) => {
		setCurrentChatId(sessionId);
		navigate(`/chat/${sessionId}`);
	};

	// Delete a chat session
	const deleteChatSession = async (sessionId: string, clientType: ChatClientType) => {
		const confirmed = window.confirm("Are you sure you want to delete this chat session?");
		if (!confirmed) return;

		try {
			await deleteChatSessionAPI(sessionId);

			// For FRIDA: navigate to new chat if deleted session is current
			// For EVA: clear selectedEvaSessionId without navigation
			if (clientType === ChatClientType.FRIDA) {
				if (currentChatId === sessionId) {
					setCurrentChatId("");
					navigate("/chat/new");
				}
				// Refresh FRIDA chat sessions list - pass selected analyst if available
				await fetchChatSessionsList(clientType, "", selectedAnalyst ? selectedAnalyst : null);
			} else if (clientType === ChatClientType.EVA) {
				if (selectedEvaSessionId === sessionId) {
					setSelectedEvaSessionId("");
				}
				// Refresh EVA chat sessions list
				await fetchEvaChatSessionsList("");
			}
		} catch (error) {
			showToast("Failed to delete chat session", "", "error");
			console.error("Error deleting chat session:", error);
		}
	};

	// Extract chat ID from URL when location changes and reset session header for "new" chats
	useEffect(() => {
		const pathParts = location.pathname.split("/");

		// FRIDA chat routes
		if (pathParts[1] === "chat") {
			const chatIdFromPath = pathParts[2];

			if (chatIdFromPath && chatIdFromPath !== "new") {
				// Existing FRIDA chat session
				setCurrentChatId(chatIdFromPath);
				// header will be set by the chat loader
			} else {
				// New FRIDA chat: clear current chat id and session header
				setCurrentChatId("");
				setCurrentSessionHeader(undefined);
			}
		}

		// EVA routes:
		if (pathParts[1] === "eva") {
			// /eva/playground: starting a new EVA chat flow, so clear any previous session header
			if (pathParts[2] === "playground") {
				setCurrentSessionHeader(undefined);
			}
			// /eva/chat/:id: existing EVA chat; do NOT clear header here (it will be set by the chat loader)
		}
	}, [location.pathname]);

	return (
		<AnalystChatContext.Provider
			value={{
				currentChatId,
				setCurrentChatId,
				currentSessionHeader,
				setCurrentSessionHeader,
				chatSessionsList,
				setChatSessionsList,
				fetchChatSessionsList,
				isLoadingChatSessions,
				evaChatSessionsList,
				setEvaChatSessionsList,
				selectedEvaSessionId,
				setSelectedEvaSessionId,
				fetchEvaChatSessionsList,
				isLoadingEvaSessions,
				analystsList,
				setAnalystsList,
				selectedAnalyst,
				setSelectedAnalyst,
				isLoadingAnalysts,
				fetchAnalystsList,
				selectChatSession,
				deleteChatSession,
				isNavbarCollapsed,
				setIsNavbarCollapsed,
			}}
		>
			{children}
		</AnalystChatContext.Provider>
	);
};

export const useAnalystChat = () => {
	const context = useContext(AnalystChatContext);
	if (!context) {
		throw new Error("useAnalystChat must be used within an AnalystChatContextProvider");
	}
	return context;
};
